import _ from "lodash";
import React, { memo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { compose } from "lib/apolloClient";
import MessagingAvatar from "../Avatar/Avatar";
import ChatInfo from "../ChatInfo/ChatInfo";
import classes from "./Thread.scss";
import ThreadAction from "../ThreadAction/ThreadAction";
import {
  getIsViewMode,
  isMessagingRevempEnabled,
  isMessagingRevampV2Enabled,
} from "MessagingV2/modules/Utils";
import { withChatOptions } from "MessagingV2/providers/ChatOptionsProvider";
import { onEnterOrSpaceKeyDown } from "Utils/AccessibilityHelpers";

const Thread = props => {
  const {
    isMainTextHighlighted,
    mainText,
    subText,
    subTextComponent,
    bottomText,
    isChatMuted,
    selected,
    timeSinceLastMessage,
    unreadMessageCount,
    profileImage,
    profileIcon,
    onlineStatus,
    onThreadClick,
    screenMode,
    id,
    // In some cases, id needs to be different than avatarId
    // eg. In search results, id should be messageId but avatarid should be chatId
    // so as to render same avatar background color for messages from one chat
    avatarId,
    containerClassName,
    showMessagingAvatar,
    channelLabel,
    isArchived,
    subTextClassName,
    customAvatarContainerClassName,
    chatType,
    userId,
    alertTypes,
    testId,
  } = props;

  const componentTestId = testId
    ? `${testId}-thread-${id}`
    : `messaging-thread-${id}`;

  const isUIRevempEnabled = isMessagingRevempEnabled();

  const threadContainerStyles = classNames({
    [classes.threadContainer]: true,
    [classes.threadContainerSelected]: selected,
    [containerClassName]: true,
  });

  const threadContainerStylesV2 = classNames(classes.threadContainerV2, {
    [classes.threadContainerSelected]: selected,
    [containerClassName]: true,
    [classes.threadContainerRevampV2]: isMessagingRevampV2Enabled(),
  });

  const isViewMode = getIsViewMode({ screenMode });

  const onThreadClickHandler = () => {
    onThreadClick(id);
  };

  const handleThreadKeyDown = e =>
    onEnterOrSpaceKeyDown(e, onThreadClickHandler);

  const channelTextContainerClass = classNames(
    classes.channelTextContainer,
    "text-body-2"
  );

  return (
    <div
      className={
        isUIRevempEnabled ? threadContainerStylesV2 : threadContainerStyles
      }
      dir="auto"
      role="button"
      tabIndex={0}
      onClick={onThreadClickHandler}
      onKeyDown={handleThreadKeyDown}
      data-test-id={componentTestId}
    >
      {channelLabel ? (
        <div className={channelTextContainerClass}>{`#${channelLabel}`}</div>
      ) : null}
      <div className={classes.threadInnerContainer}>
        {selected && !isUIRevempEnabled ? (
          <div className={classes.sideStrip} />
        ) : null}
        {showMessagingAvatar ? (
          <div
            className={classNames(
              classes.avatarContainer,
              customAvatarContainerClassName
            )}
          >
            <MessagingAvatar
              id={avatarId}
              name={mainText}
              url={profileImage}
              icon={profileIcon}
              onlineStatus={onlineStatus}
              chatType={chatType}
              isArchived={isArchived}
            />
          </div>
        ) : null}
        <ChatInfo
          isMainTextHighlighted={isMainTextHighlighted}
          mainText={mainText}
          subText={subText}
          subTextComponent={subTextComponent}
          bottomText={bottomText}
          subTextClassName={subTextClassName}
          userId={userId}
          alertTypes={alertTypes}
        />
        {!isViewMode ? (
          <ThreadAction
            id={id}
            timeSinceLastMessage={timeSinceLastMessage}
            unreadMessageCount={unreadMessageCount}
            isChatMuted={isChatMuted}
            isArchived={isArchived}
            selected={selected}
          />
        ) : null}
      </div>
    </div>
  );
};

Thread.propTypes = {
  isMainTextHighlighted: PropTypes.bool,
  mainText: PropTypes.string,
  subText: PropTypes.string,
  bottomText: PropTypes.string,
  isChatMuted: PropTypes.bool,
  timeSinceLastMessage: PropTypes.string,
  unreadMessageCount: PropTypes.number,
  selected: PropTypes.bool,
  profileImage: PropTypes.string,
  profileIcon: PropTypes.string,
  onlineStatus: PropTypes.bool,
  onThreadClick: PropTypes.func,
  containerClassName: PropTypes.string,
  subTextClassName: PropTypes.string,
  showMessagingAvatar: PropTypes.bool,
  avatarId: PropTypes.string,
  onRemoveClick: PropTypes.func,
  chatType: PropTypes.string,
};

Thread.defaultProps = {
  isMainTextHighlighted: false,
  isChatMuted: false,
  unreadMessageCount: 0,
  selected: false,
  onlineStatus: false,
  onThreadClick: _.noop,
  showMessagingAvatar: true,
  avatarId: null,
  onRemoveClick: _.noop,
  profileIcon: null,
};

const selector = props => ({
  screenMode: props.screenMode,
});

export default compose(withChatOptions({ selector }), memo)(Thread);
